<template>
  <effect-expression
    v-slot="{ isSpecialEffect, value, isValid, expression, expressionContext }"
    :expression="cost.expression"
    :parse-context="expressionParseContext"
    :rounds="rounds"
    :sign-required="true"
  >
    <span
      v-bind="$attrs"
      ref="numberElement"
      class="action-cost-amount"
      @pointerenter="onHover"
      @pointerleave="onHoverOff"
    >
      <abbr
        v-if="isSpecialEffect && !inlineExpression"
        :class="{ 'action-cost-amount__invalid': !isValid }"
      >
        {{ value }}&thinsp;<span class="action-cost-amount__unit">{{
          affectedElement?.unit
        }}</span>
      </abbr>
      <span v-else :class="{ 'action-cost-amount__invalid': !isValid }">
        {{ value }}&thinsp;<span class="action-cost-amount__unit">{{
          affectedElement?.unit
        }}</span>
      </span>
      <span v-if="showItem">
        {{ affectedElement?.title }}
      </span>

      <teleport
        v-if="!inlineExpression && isSpecialEffect && state.hovered"
        to="#app"
      >
        <transition name="transition-action-cost-amount" appear>
          <expression-rendered
            :expression="expression"
            :parse-context="expressionContext"
            class="action-cost-amount__expression -popover"
            :style="{ top: `${state.y}px`, left: `${state.x}px` }"
          ></expression-rendered>
        </transition>
      </teleport>
    </span>
    <div
      v-if="inlineExpression"
      class="action-cost-amount__expressionblock"
    >
      <expression-rendered
        v-if="isSpecialEffect"
        :expression="expression"
        :parse-context="expressionContext"
        class="action-cost-amount__expression -inline"
      ></expression-rendered>
    </div>
  </effect-expression>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";

import EffectExpression from "../effect/EffectExpression.vue";
import ExpressionRendered from "../expression/ExpressionRendered.vue";

import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useElements } from "../element/elements";

import type { IndexItem } from "../index/IndexItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type { TargetItem } from "../target/TargetItem";
import type { ActionCostItem } from "./ActionCostItem";
import type { ExpressionParseContext } from "../effect/Expression";

const props = withDefaults(defineProps<{
  cost: ActionCostItem;
  showItem?: boolean;
  rounds?: Partial<SimulationRoundItem>[];
  roundIndex?: number;
  inlineExpression?: boolean;
  resources?: Record<string, ResourceItem>;
  targets?: Record<string, TargetItem>;
  indices?: Record<string, IndexItem>;
}>(), {
  showItem: false,
  inlineExpression: false
});

const elements = useElements();

const numberElement = ref<HTMLSpanElement>();


const state = reactive({
  hovered: false,
  x: 0,
  y: 0,
  listVisible: false
});

const contextResources = computed(() => {
  return props.resources ?? useResourceStore().items;
});
const contextTargets = computed(() => {
  return props.targets ?? useTargetStore().items;
});
const contextIndices = computed(() => {
  return props.indices ?? useIndexStore().items;
});

const affectedElement = computed(() => {
  if (!props.cost) return null;
  return elements.byUid[props.cost.elementUid] ?? null;
});

const expressionParseContext = computed(() => {
  const context: ExpressionParseContext = {
    affectedType: affectedElement.value?.type,
    affectedId: affectedElement.value?.id,
    targets: contextTargets.value,
    resources: contextResources.value,
    indices: contextIndices.value
  };

  if (props.rounds) {
    context.rounds = props.rounds;
  }

  if (props.roundIndex != null) {
    context.roundIndex = props.roundIndex;
  } else {
    context.roundIndex = props.rounds?.[props.rounds.length - 1]?.index ?? 0;
  }

  return context;
});
    
function onHover() {
  if (props.inlineExpression) return;
  state.hovered = true;

  if (numberElement.value) {
    const rect = numberElement.value.getBoundingClientRect();
    state.x = rect.left;
    state.y = rect.top + rect.height + 2;
  }
}
function onHoverOff() {
  if (props.inlineExpression) return;
  state.hovered = false;
}
</script>

<style lang="scss">
.action-cost-amount {
  white-space: nowrap;
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: flex-end;

  &__invalid {
    color: var(--color-error);
  }

  abbr {
    text-decoration: dotted underline;
    text-underline-offset: 2px;
    text-decoration-color: var(--color-text-medium);
  }

  &__unit {
    font-size: 0.75rem;
    color: var(--color-text-medium);
    display: none;
    // TODO: include this
  }

  &__expression {
    white-space: nowrap;
    display: inline-block;
    font-family: var(--font-family-mono);
    background-color: #fbfbff;
    padding: 3px;
    border-radius: 4px;
    border: 1px solid var(--color-border);
    border-left-width: 3px;
    font-size: 11px;
    cursor: default;

    &.-inline {
      position: static;
      pointer-events: auto;
      margin-left: 0.25rem;
    }

    &.-popover {
      position: absolute;
      pointer-events: none;
    }
  }

  &__expressionblock {
    white-space: nowrap;
    display: inline-block;
  }
}

.transition-action-cost-amount {
  &-enter-active,
  &-leave-active {
    transition:
      opacity 100ms ease-out,
      transform 100ms ease-out;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }
}
</style>